import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/http';
import { getCampaignIntegrityUserMessage } from '@/lib/campaignIntegrityErrors';
import { useToast } from '@/hooks/use-toast';

// NOTE: This page displays campaign snapshots contained in locked cycles.
// Backend still uses /api/campaign-snapshots endpoints; frontend renames for clarity.
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Eye, Calendar, Package, Loader2, Archive, AlertTriangle } from 'lucide-react';
import { SimpleDetailsDialog } from '@/components/SimpleDetailsDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dbg } from '@/lib/dbg';

interface CampaignSnapshot {
  id: number;
  campaignId: number;
  campaignName: string;
  cycleId: number;
  createdAt: string;
}

interface CycleSnapshot {
  id: string;
  cycleId: number;
  cycleName?: string;
  createdAt: string;
  createdByUserId: string;
  note: string;
  locked?: boolean;
  hasCampaignSnapshots?: boolean;
  status?: 'locked' | 'empty-lock' | 'unlocked';
}

interface SnapshotDetails {
  id: number;
  campaignId: number;
  campaignName: string;
  cycleId: number;
  packs: any[];
  stores: any[];
  operators: any[];
  createdAt: string;
}

export default function CycleSnapshotsPage() {
  const [selectedSnapshot, setSelectedSnapshot] = useState<CampaignSnapshot | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all campaign snapshots
  const snapshotsQuery = useQuery<CampaignSnapshot[]>({
    queryKey: ['/api/campaign-snapshots'],
    queryFn: async () => {
      const res = await apiFetch('/api/campaign-snapshots');
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
  });

  // Fetch all cycle snapshots (from locked_cycles) - OPTIMIZED: single API call
  const cycleSnapshotsQuery = useQuery<CycleSnapshot[]>({
    queryKey: ['/api/locked-cycles'],
    queryFn: async () => {
      const res = await apiFetch('/api/locked-cycles');
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const lockedCycles = await res.json();

      // Map to CycleSnapshot interface
      return lockedCycles.map((lc: any) => ({
        id: String(lc.id),
        cycleId: lc.cycleId,
        cycleName: lc.cycleName,
        createdAt: lc.lockedAt,
        createdByUserId: String(lc.lockedBy ?? ''),
        note: '',
        locked: true,
        hasCampaignSnapshots: lc.hasCampaignSnapshots,
        status: lc.status,
      }));
    },
  });


  // Fetch details for selected snapshot
  const detailsQuery = useQuery<SnapshotDetails>({
    queryKey: [`/api/campaign-snapshots/${selectedSnapshot?.id}/details`],
    queryFn: async () => {
      const res = await apiFetch(`/api/campaign-snapshots/${selectedSnapshot?.id}/details`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    enabled: !!selectedSnapshot,
  });

  // Lock cycle mutation with enhanced feedback
  const lockMutation = useMutation({
    mutationFn: async (cycleId: number) => {
      const response = await fetch(`/api/cycles/${cycleId}/lock`, { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to lock cycle: ${response.statusText}`);
      }
      return response.json();
    },
    onSuccess: async (_data, cycleId) => {
      // Use the assignment count from the lock response
      const count = _data?.assignmentCount || 0;
      const totalCampaigns = _data?.totalCampaigns || 0;

      if (totalCampaigns > 0) {
        toast({
          title: '✅ Cycle Locked Successfully',
          description: `Cycle locked with ${count} assignments across ${totalCampaigns} campaigns.`,
          duration: 4000
        });
      } else {
        toast({
          title: '⚠️ Cycle Locked (Empty)',
          description: 'Cycle locked, but no confirmed campaigns were found.',
          variant: 'destructive',
          duration: 6000
        });
      }

      // Refetch data
      queryClient.invalidateQueries({ queryKey: ['/api/cycle-snapshots'] });
    },
    onError: (error: any) => {
      toast({
        title: '❌ Failed to Lock Cycle',
        description: getCampaignIntegrityUserMessage(error, 'cycle-lock'),
        variant: 'destructive',
        duration: 5000
      });
    }
  });

  // Unlock cycle mutation with feedback
  const unlockMutation = useMutation({
    mutationFn: async (cycleId: number) => {
      const response = await fetch(`/api/cycles/${cycleId}/unlock`, { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to unlock cycle: ${response.statusText}`);
      }
      return response.json();
    },
    onSuccess: (_data, cycleId) => {
      toast({
        title: '✅ Cycle Unlocked Successfully',
        description: 'The cycle has been unlocked and is now available for editing.',
        duration: 4000
      });

      // Refetch data
      queryClient.invalidateQueries({ queryKey: ['/api/cycle-snapshots'] });
    },
    onError: (error: any) => {
      const message = error?.message || 'Please try again.';
      toast({
        title: '❌ Failed to Unlock Cycle',
        description: message,
        variant: 'destructive',
        duration: 5000
      });
    }
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleViewDetails = (snapshot: CampaignSnapshot) => {
    setSelectedSnapshot(snapshot);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedSnapshot(null);
  };

  const isLoading = snapshotsQuery.isLoading || cycleSnapshotsQuery.isLoading;
  const hasError = snapshotsQuery.error || cycleSnapshotsQuery.error;

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading snapshots...</span>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-500">
              Error loading snapshots: {(snapshotsQuery.error || cycleSnapshotsQuery.error)?.message || 'Unknown error'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const snapshots = snapshotsQuery.data || [];
  const cycleSnapshots = cycleSnapshotsQuery.data || [];

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Snapshots Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="campaign-snapshots" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="campaign-snapshots" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Campaign Snapshots
                <Badge variant="secondary">{snapshots.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="cycle-snapshots" className="flex items-center gap-2">
                <Archive className="h-4 w-4" />
                Cycle Snapshots
                <Badge variant="secondary">{cycleSnapshots.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="campaign-snapshots" className="mt-6">
              {snapshots.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No campaign snapshots available.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Campaign Name</TableHead>
                      <TableHead>Cycle ID</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {snapshots.map((snapshot) => (
                      <TableRow key={snapshot.id}>
                        <TableCell className="font-mono">{snapshot.id}</TableCell>
                        <TableCell>{snapshot.campaignName}</TableCell>
                        <TableCell className="font-mono">{snapshot.cycleId}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="h-3 w-3" />
                            {formatDate(snapshot.createdAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(snapshot)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="cycle-snapshots" className="mt-6">
              {cycleSnapshots.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No cycle snapshots available.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Cycle Name</TableHead>
                      <TableHead>Cycle ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cycleSnapshots.map((cycleSnapshot) => (
                      <TableRow key={cycleSnapshot.id}>
                        <TableCell className="font-mono">{cycleSnapshot.id}</TableCell>
                        <TableCell>{cycleSnapshot.cycleName || `Cycle ${cycleSnapshot.cycleId}`}</TableCell>
                        <TableCell className="font-mono">{cycleSnapshot.cycleId}</TableCell>
                        <TableCell>
                          {cycleSnapshot.hasCampaignSnapshots === false ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Locked (Empty)
                              </Badge>
                              <div
                                className="text-xs text-muted-foreground cursor-help"
                                title="This cycle was locked without capturing campaign snapshots. Unlock and re-lock to resolve."
                              >
                                ⚠️
                              </div>
                            </div>
                          ) : (
                            <Badge variant="success">
                              Locked
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="h-3 w-3" />
                            {formatDate(cycleSnapshot.createdAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <SimpleDetailsDialog cycleId={cycleSnapshot.cycleId} triggerLabel="Details" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => unlockMutation.mutate(cycleSnapshot.cycleId)}
                              disabled={lockMutation.isPending || unlockMutation.isPending}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Unlock this cycle"
                            >
                              {unlockMutation.isPending ? 'Unlocking...' : 'Unlock'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={handleCloseDetails}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Snapshot Details - ID {selectedSnapshot?.id}
            </DialogTitle>
          </DialogHeader>

          {detailsQuery.isLoading && (
            <div className="p-4 flex items-center justify-center">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading snapshot details...</span>
              </div>
            </div>
          )}

          {detailsQuery.error && (
            <div className="p-4 text-red-500">
              Error loading details: {detailsQuery.error?.message || 'Unknown error'}
            </div>
          )}

          {detailsQuery.data && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{detailsQuery.data.packs?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Packs</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{detailsQuery.data.stores?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Stores</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{detailsQuery.data.operators?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Operators</p>
                  </CardContent>
                </Card>
              </div>

              {/* Raw JSON Data */}
              <Card>
                <CardHeader>
                  <CardTitle>Raw Snapshot Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="bg-gray-100 p-4 rounded-md overflow-auto text-xs max-h-96">
                    {JSON.stringify(detailsQuery.data, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
